// Deal Submission spec §10 — per-section editor for the
// document-request items. One row = one document = one upload control
// at runtime (FL-13). Authoring lives here, not in the form-builder:
// data sections collect structured data only.

import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { Button, Input, Textarea } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import type { DocumentTagDto } from "@/api/reportsV32";

export interface DocumentRequestDraft {
  /** Synthetic id for list keying within the editor; survives reorder. */
  id: string;
  /** Persisted document-request id; null on unsaved/new rows. Gates the
   *  per-document AI prompt editor (prompts key on the saved id). */
  serverId: string | null;
  key: string;
  label: string;
  required: boolean;
  allowedTypes: string;
  documentTagCode: string;
  destinationRule: string;
  instructions: string;
  // AI evaluation — per-document toggles saved with the template.
  feedbackEnabled: boolean;
  assessmentEnabled: boolean;
}

interface DealSubmissionDocumentRequestListProps {
  items: DocumentRequestDraft[];
  onChange: (next: DocumentRequestDraft[]) => void;
  documentTags: ReadonlyArray<DocumentTagDto>;
}

const ALLOWED_TYPE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "pdf", label: "PDF only" },
  { value: "pdf,docx", label: "PDF + Word" },
  { value: "pdf,xlsx", label: "PDF + Excel" },
  { value: "pdf,docx,xlsx", label: "PDF + Word + Excel" },
  { value: "pdf,png,jpg,jpeg", label: "PDF + image" },
  { value: "", label: "Any (no restriction)" },
];

export function DealSubmissionDocumentRequestList({
  items,
  onChange,
  documentTags,
}: DealSubmissionDocumentRequestListProps) {
  function add() {
    const next: DocumentRequestDraft = {
      id: crypto.randomUUID(),
      serverId: null,
      key: `document_${items.length + 1}`,
      label: "New document",
      required: false,
      allowedTypes: "pdf",
      documentTagCode: "",
      destinationRule: "",
      instructions: "",
      feedbackEnabled: true,
      assessmentEnabled: true,
    };
    onChange([...items, next]);
  }

  function update(i: number, patch: Partial<DocumentRequestDraft>) {
    onChange(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  }

  function remove(i: number) {
    onChange(items.filter((_, idx) => idx !== i));
  }

  function move(i: number, dir: -1 | 1) {
    const target = i + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[i], next[target]] = [next[target], next[i]];
    onChange(next);
  }

  return (
    <div className="flex flex-col gap-space-3" data-testid="document-request-list">
      {items.length === 0 ? (
        <p className="text-small text-gray-mid italic">
          No document requests on this section yet. Each request becomes a
          separate upload control at runtime.
        </p>
      ) : (
        <ol className="flex flex-col gap-space-3 list-none p-0">
          {items.map((it, i) => (
            <li
              key={it.id}
              className="rounded-md border border-border-light p-space-3 bg-white-pure"
              data-testid={`document-request-row-${i}`}
            >
              <div className="flex items-center justify-between gap-space-2 mb-space-2">
                <span className="text-caption uppercase tracking-wide text-gray-mid">
                  Document #{i + 1}
                </span>
                <div className="flex items-center gap-space-1">
                  <Button
                    variant="secondary"
                    size="sm"
                    type="button"
                    aria-label="Move up"
                    onClick={() => move(i, -1)}
                    disabled={i === 0}
                  >
                    <ArrowUp size={14} />
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    type="button"
                    aria-label="Move down"
                    onClick={() => move(i, 1)}
                    disabled={i === items.length - 1}
                  >
                    <ArrowDown size={14} />
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    type="button"
                    aria-label="Remove document request"
                    onClick={() => remove(i)}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
                <label className="flex flex-col gap-space-1 text-small text-onyx">
                  <span>
                    Label <span className="text-error">*</span>
                  </span>
                  <Input
                    value={it.label}
                    onChange={(e) => update(i, { label: e.target.value })}
                  />
                </label>
                <label className="flex flex-col gap-space-1 text-small text-onyx">
                  <span>
                    Key (stable identifier) <span className="text-error">*</span>
                  </span>
                  <Input
                    value={it.key}
                    onChange={(e) => update(i, { key: e.target.value })}
                  />
                  <span className="text-caption text-gray-mid">
                    Lowercase letters / digits / hyphens. Referenced by import
                    rules; unique within the template version.
                  </span>
                </label>
                <label className="flex flex-col gap-space-1 text-small text-onyx">
                  <span>Allowed file types</span>
                  <Select
                    value={it.allowedTypes}
                    onChange={(e) => update(i, { allowedTypes: e.target.value })}
                  >
                    {ALLOWED_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                </label>
                <label className="flex flex-col gap-space-1 text-small text-onyx">
                  <span>Document tag (for filing)</span>
                  <Select
                    value={it.documentTagCode}
                    onChange={(e) =>
                      update(i, { documentTagCode: e.target.value })
                    }
                  >
                    <option value="">(none — uses default)</option>
                    {documentTags
                      .filter((t) => t.isActive)
                      .map((t) => (
                        <option key={t.tagCode} value={t.tagCode}>
                          {t.displayName} ({t.tagCode})
                        </option>
                      ))}
                  </Select>
                </label>
                <label className="flex flex-col gap-space-1 text-small text-onyx md:col-span-2">
                  <span>Destination / routing rule (optional, JSON or token)</span>
                  <Input
                    value={it.destinationRule}
                    onChange={(e) =>
                      update(i, { destinationRule: e.target.value })
                    }
                    placeholder='e.g. {"folder":"Financials"}'
                  />
                  <span className="text-caption text-gray-mid">
                    Consumed by the import engine to decide where this document
                    files on the new Deal. Leave blank to use the document tag's
                    default destination.
                  </span>
                </label>
                <label className="flex items-center gap-space-2 text-small text-onyx">
                  <input
                    type="checkbox"
                    checked={it.required}
                    onChange={(e) => update(i, { required: e.target.checked })}
                  />
                  Required to submit
                </label>
                <label className="flex flex-col gap-space-1 text-small text-onyx md:col-span-2">
                  <span>Broker-facing instructions</span>
                  <Textarea
                    rows={2}
                    value={it.instructions}
                    onChange={(e) =>
                      update(i, { instructions: e.target.value })
                    }
                  />
                </label>
              </div>
            </li>
          ))}
        </ol>
      )}

      <div>
        <Button variant="secondary" size="sm" type="button" onClick={add}>
          <Plus size={14} className="mr-space-1" /> Add document request
        </Button>
      </div>
    </div>
  );
}
